import React, { useEffect, useState } from 'react';
import { withFormik } from 'formik';
import * as yup from 'yup';
import { ridesumLogo } from '../../../components/assets';
import { t } from '../../../i18n';
import Button from '../../../components/button';
import { postResetPassword, postValidatePasswordResetToken } from '../../../backend-api';
import PasswordResetError from './PasswordResetError';
import { createLogger } from '../../../utilities/log';
import { RIDER } from '../../../constants';
import UrlTo from '../../../url-to';
import { store } from '../../../router';
import { logIn } from '../../../login/actions';
import SendNewLink from './send-new-link';
import './styles.scss';

const logger = createLogger('ResetPassword');

const validationSchema = yup.object().shape({
  password: yup.string().required('PASSWORD_REQUIRED'),
  confirmPassword: yup
    .string()
    .required('PASSWORD_REQUIRED')
    .oneOf([yup.ref('password')], 'DIGITAL_COURSES.RESET_PASSWORD.PASSWORDS_DONT_MATCH'),
});

const ResetPassword = ({
  history,
  values: { token, returnUrl, email },
  status: { serverError },
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  isSubmitting,
}) => {
  const [suggestSendingNewLink, setSuggestSendingNewLink] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      try {
        await postValidatePasswordResetToken(token);
      } catch (e) {
        const errorCode = e.response?.data.error;
        if (errorCode === PasswordResetError.ALREADY_ACTIVATED) {
          history.replace(UrlTo.login({ role: RIDER, returnUrl }));
          logger.info('Link has already been activated', { errorCode, token });
          return;
        }
        if (errorCode === PasswordResetError.EXPIRED) {
          setSuggestSendingNewLink(errorCode);
          logger.info('Link expired', { errorCode, token });
          return;
        }
        logger.error('Unexpected token validation error', { message: e.message, token });
      }
    };
    validateToken();
  }, []);

  return (
    <div className="reset-password">
      <div className="reset-password__background" />
      <div className="reset-password__main">
        <div className="reset-password__header">
          <img className="reset-password__logo" src={ridesumLogo} alt="Ridesum logo" />
          <h1 className="reset-password__tagline">{t('RIDESUM_SLOGAN')}</h1>
        </div>
        {!suggestSendingNewLink && (
          <form>
            <p className="reset-password__instructions">{t('DIGITAL_COURSES.RESET_PASSWORD.CREATE_PASSWORD')}</p>
            {/* Hidden email enables Chrome's Password Manager to save both login & password,
                so users don't need to enter the email when they log in */}
            <input
              type="email"
              id="username"
              name="username"
              value={email}
              className="reset-password__username"
              readOnly
            />

            <div className="reset-password__input-group">
              <label htmlFor="password" className="reset-password__input-label">
                {t('PASSWORD')}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                placeholder={t('PASSWORD')}
                className="reset-password__text-input"
                autoComplete="new-password"
                onChange={handleChange}
                onBlur={handleBlur}
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
              {touched.password && errors.password && (
                <strong className="reset-password__input-error">{t(errors.password)}</strong>
              )}
            </div>
            <div className="reset-password__input-group">
              <label htmlFor="confirmPassword" className="reset-password__input-label">
                {t('DIGITAL_COURSES.RESET_PASSWORD.CONFIRM_PASSWORD')}
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                placeholder={t('DIGITAL_COURSES.RESET_PASSWORD.CONFIRM_PASSWORD')}
                className="reset-password__text-input"
                autoComplete="new-password"
                onChange={handleChange}
                onBlur={handleBlur}
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
              {touched.confirmPassword && errors.confirmPassword && (
                <strong className="reset-password__input-error">{t(errors.confirmPassword)}</strong>
              )}
            </div>
            {serverError && <strong className="reset-password__reset-error">{t('SERVER_ERROR')}</strong>}
            <Button
              text={t('CONTINUE')}
              onClick={handleSubmit}
              className="reset-password__submit-button"
              disabled={isSubmitting}
            />
          </form>
        )}
        {!!suggestSendingNewLink && <SendNewLink token={token} />}
      </div>
    </div>
  );
};

export default withFormik({
  mapPropsToValues() {
    const query = new URLSearchParams(window.location.search);
    const token = query.get('token');
    const returnUrl = query.get('returnUrl');
    const email = query.get('email');
    const role = query.get('role');

    return {
      password: '',
      confirmPassword: '',
      email,
      role,
      token,
      returnUrl,
    };
  },
  mapPropsToStatus() {
    return {
      serverError: false,
    };
  },
  validationSchema,
  async handleSubmit({ email, role, password, token, returnUrl }, { setStatus, setSubmitting }) {
    setStatus({ serverError: false });
    try {
      await postResetPassword({ token, newPassword: password });
      await store.dispatch(logIn({ email, role, password, returnUrl }));
    } catch (e) {
      logger.error('Unexpected error when submitting new password', { message: e.message });
      setStatus({ serverError: true });
    }
    setSubmitting(false);
  },
})(ResetPassword);
